import { getInstance } from './instance';
import { OperatorIdentity, operatorRequestConfig } from './operatorHeaders';

const instance = getInstance();

interface GenerateReferralMagazineData {
  payload: {
    month: string;
    year: number;
    userEmail: string;
    customerId: string;
  };
  isPreview: boolean;
}

type QueryKeyType = [unknown, string, string, string];

interface UpdateReferralMagazineRequest {
  month: string;
  year: string;
  customerId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
}

const getAllMagazineMetrics = (month: string, year: number) => {
  const url = '/referral-marketing/magazines/reports/all';

  const params = { month, year };

  return instance
    .get(url, { params })
    .then((res) => res.data)
    .catch((error) => error);
};

const getRmMagazineMetricsEditing = (
  month: string,
  year: number,
  page = 1,
  perPage = 10,
  status: string,
  sortByFilter?: { [key: string]: string },
) => {
  const url = '/abo/reports/magazine-editing';
  const params = { month, year, page, perPage, status };
  if (sortByFilter) {
    params.sortBy = `${sortByFilter.key}:${sortByFilter.value}`;
  }

  return instance
    .get(url, { params })
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const getRmMagazineMetricsSentToPrint = (
  month: string,
  year: number,
  page = 1,
  perPage = 10,
  status: string,
  sortByFilter?: { [key: string]: string },
) => {
  const url = '/abo/reports/magazine-sent-to-print';
  const params = { month, year, page, perPage, status };
  if (sortByFilter) {
    params.sortBy = `${sortByFilter.key}:${sortByFilter.value}`;
  }

  return instance
    .get(url, { params })
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const getRmMagazineMetricsMagazineGenerated = (
  month: string,
  year: number,
  page = 1,
  perPage = 10,
  status: string,
  sortByFilter?: { [key: string]: string },
) => {
  const url = '/abo/reports/all';
  const params = { month, year, page, perPage, status };
  if (sortByFilter) {
    params.sortBy = `${sortByFilter.key}:${sortByFilter.value}`;
  }

  return instance
    .get(url, { params })
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const getRmMagazineMetrics = (month: string, year: number) => {
  const url = '/abo/reports';
  const params = { month, year };

  return instance
    .get(url, { params })
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const getRmMagazineMetricsTableData = (
  month: string,
  year: number,
  page = 1,
  perPage = 10,
  status: string,
  sortByFilter?: { [key: string]: string },
) => {
  const url = '/abo/table-reports';
  const params = { month, year, page, perPage, status };
  if (sortByFilter) {
    params.sortBy = `${sortByFilter.key}:${sortByFilter.value}`;
  }

  return instance
    .get(url, { params })
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const getRmMagazineMetricsBySearch = (
  searchQuery: string,
  month: string,
  year: number,
  page = 1,
  perPage = 10,
  status: string,
  sortByFilter?: { [key: string]: string },
) => {
  const url = '/abo/metrics/reports/search';
  const params = {
    searchQuery,
    month,
    year,
    page,
    perPage,
    status,
  };
  if (sortByFilter) {
    params.sortBy = `${sortByFilter.key}:${sortByFilter.value}`;
  }

  return instance
    .get(url, { params })
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const postRmMagazineGenerate = (
  month: string,
  year: number,
  userEmail: string,
  customerId: string,
  isPreview: boolean,
) => {
  const url = '/admin/referral-marketing/generate-magazine';
  const params = {
    month,
    year,
    userEmail,
    customerId,
    isPreview,
  };
  return instance
    .post(url, params)
    .then((res) => res)
    .catch((error) => Promise.reject(error));
};

const generateReferralMagazineAdmin = (data: GenerateReferralMagazineData) => {
  return postRmMagazineGenerate(
    data.payload.month,
    data.payload.year,
    data.payload.userEmail,
    data.payload.customerId,
    data.isPreview,
  );
};

export const uploadCustomCover = (body = {}) => {
  const url = '/abo/upload-custom-cover';
  return instance
    .post(url, body)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const startMonthlyTurnOver = (body = {}) => {
  const url = 'admin/referral-marketing/monthly-turn-over';
  return instance
    .post(url, body)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const getMonthlyTurnOverStatus = () => {
  const url = 'admin/referral-marketing/monthly-turn-over';

  return instance
    .get(url)
    .then((res) => res.data)
    .catch((error) => error);
};

const schedulePermanentLinkTurnOver = (body = {}) => {
  const url = 'admin/referral-marketing/permanent-link-turn-over';
  return instance
    .post(url, body)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const getSchedulePermanentLinkTurnOver = () => {
  const url = 'admin/referral-marketing/permanent-link-turn-over';

  return instance
    .get(url)
    .then((res) => res.data)
    .catch((error) => error);
};

const getMagazineAdmin = ({ queryKey }: { queryKey: QueryKeyType }) => {
  const [, year, month, customerId] = queryKey;
  return instance
    .get(`/admin/referral-marketing/${year}/${month}/${customerId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

export const getGeneratedMagazineAdmin = ({
  queryKey,
}: {
  queryKey: QueryKeyType;
}) => {
  const [, year, month, customerId] = queryKey;
  return instance
    .get(
      `/admin/referral-marketing/generated-magazines/${year}/${month}/${customerId}`,
    )
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const updateReferralMagazineAdmin = (req: UpdateReferralMagazineRequest) => {
  const { month, year, customerId, ...rest } = req;

  return instance
    .patch(
      `/admin/referral-marketing/${year}/${month}/${customerId}`,
      rest?.payload,
    )
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

/**
 * Files the magazine print job on the member's behalf.
 *
 * `actorEmail` names the operator doing it. Digital Services records it on the
 * print job's append-only creation history, and without it every job files as
 * an unattributed `abo-admin`: this back office authenticates to DS with a
 * shared api key, so the request carries no user of its own for DS to read.
 *
 * Sent as a header on THIS call only, never on the shared axios instance. DS
 * also reads `x-afy-user-email` to resolve which MEMBER an api-key request is
 * about, so attaching it globally would make every other back-office request
 * claim the operator is the customer.
 */
const createSetupTicket = ({
  id,
  customerId,
  actorEmail,
}: {
  id: string;
  customerId: string;
  actorEmail?: string;
}) => {
  return instance
    .post(
      `/admin/referral-marketing/${id}/${customerId}/create-ticket`,
      {},
      actorEmail ? { headers: { "x-afy-user-email": actorEmail } } : undefined,
    )
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const getMagazine = async (year: number, month: string) => {
  try {
    const response = await instance.get(
      `/referral-marketing/magazines/${year}/${month}`,
    );
    return response.data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

const getCustomerDetails = async (email: string) => {
  try {
    const response = await instance.get(`/customers/find/filter/`, {
      params: { query: email },
    });
    return response.data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

const getMagazineRecipients = async (page, perPage, keyword) => {
  try {
    const response = await instance.get(
      `customers/count-leads?&page=${page}&perPage=${perPage}&nameOrEmail=${keyword}&isRMDeliverable=true`,
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const getMagazineRecipientLeads = async (
  customerId,
  page,
  perPage,
  keyword,
) => {
  try {
    const response = await instance.get(
      `customers/get-leads/${customerId}?page=${page}&perPage=${perPage}&nameOrEmail=${keyword}&isRMDeliverable=true`,
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

/**
 * The CSV exports and the member read below send the signed-in operator
 * (`operator`) so Digital Services records who exported what
 * (abo-data-access-guard task 05).
 */
const getAllCSVLeads = async (operator?: OperatorIdentity) => {
  try {
    const response = await instance.post(
      `email-campaigns/leads/export-all-leads`,
      undefined,
      operatorRequestConfig(operator),
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const getOneCSVLead = async (
  customerId: string,
  operator?: OperatorIdentity,
) => {
  try {
    const response = await instance.get(
      `email-campaigns/leads/export-leads/${customerId}`,
      operatorRequestConfig(operator),
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const getOneCustomerDetail = async (
  customerId: string,
  operator?: OperatorIdentity,
) => {
  try {
    const response = await instance.get(
      `customers/find-one/${customerId}`,
      operatorRequestConfig(operator),
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

export {
  getAllMagazineMetrics,
  getRmMagazineMetrics,
  getRmMagazineMetricsBySearch,
  getRmMagazineMetricsEditing,
  getRmMagazineMetricsSentToPrint,
  getRmMagazineMetricsMagazineGenerated,
  postRmMagazineGenerate,
  generateReferralMagazineAdmin,
  startMonthlyTurnOver,
  getMonthlyTurnOverStatus,
  schedulePermanentLinkTurnOver,
  getSchedulePermanentLinkTurnOver,
  getMagazineAdmin,
  updateReferralMagazineAdmin,
  createSetupTicket,
  getMagazine,
  getCustomerDetails,
  getRmMagazineMetricsTableData,
  getMagazineRecipients,
  getMagazineRecipientLeads,
  getAllCSVLeads,
  getOneCSVLead,
  getOneCustomerDetail,
};
